import React, { useContext, useEffect, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import '../../../css/FlyoutTeacherAIScoreResultsPanel.scss';

import { FlyoutOpenState } from '../../../Constants';

import { register } from '../../../i18n';

import navMenuService from '../../services/NavMenuService';
import scoringService from '../../services/ScoringService';

import useAccessibilityClick from '../../../hooks/useAccessibilityClick';

// import AiSuggestedScoreButton from '../AiSuggestedScoreButton';
import ExpandableRubricScoringTable from './ExpandableRubricScoringTable';
import ExpandableStandardsScoringTable from './ExpandableStandardsScoringTable';

const t = register('TeacherAIScoreResultsPanel');
// const t2 = register('AriaLabels');

const TeacherAIScoreResultsPanel = observer(({ lessonElementId } = {}) => {
  const {
    lessonManager,
    responseManager,
    scoringManager
  } = useContext(MobXProviderContext);

  const lessonElementState = responseManager.getLessonElementState(lessonElementId);
  const model = lessonManager.getSlideModel(lessonElementId);
  const maxScore = lessonElementState.maxScore ? parseFloat(lessonElementState.maxScore) : 0;

  // When tempScores Map has a value, use it (actively being edited or initialized from saved score)
  // Otherwise, fallback to the submitted score (for cases where tempScores has not been initialized)
  // Note: getTempScore returns '0' when the Map does not have the lessonElementId key
  const displayScore = scoringManager.tempScores?.has?.(lessonElementId) ?
    scoringManager.getTempScore(lessonElementId) :
    (lessonElementState.submittedItemScore || '0');
  const [aiScoreData, setAiScoreData] = useState([]);
  const [isRubricExpanded, setIsRubricExpanded] = useState(false);
  const [isStandardsExpanded, setIsStandardsExpanded] = useState(false);

  const rubricToggleButtonRef = useRef();
  const standardsToggleButtonRef = useRef();

  // On mount, if both sections are collapsed, reset flyout to OPEN
  useEffect(() => {
    if (!isRubricExpanded && !isStandardsExpanded) {
      const closeFirst = false;
      navMenuService.toggleFlyoutPanel(closeFirst, FlyoutOpenState.OPEN, lessonElementId);
    }
  }, []);

  useAccessibilityClick(rubricToggleButtonRef, () => {
    const closeFirst = false;
    const _isRubricExpanded = !isRubricExpanded;
    setIsRubricExpanded(_isRubricExpanded);

    // If expanding rubric or standards is already expanded, make flyout OPEN_WIDER
    // If collapsing rubric and standards is collapsed, make flyout OPEN
    if (_isRubricExpanded || isStandardsExpanded) {
      navMenuService.toggleFlyoutPanel(closeFirst, FlyoutOpenState.OPEN_WIDER, lessonElementId);
    } else {
      navMenuService.toggleFlyoutPanel(closeFirst, FlyoutOpenState.OPEN, lessonElementId);
    }
  });

  useAccessibilityClick(standardsToggleButtonRef, () => {
    const closeFirst = false;
    const _isStandardsExpanded = !isStandardsExpanded;
    setIsStandardsExpanded(_isStandardsExpanded);

    // If expanding standards or rubric is already expanded, make flyout OPEN_WIDER
    // If collapsing standards and rubric is collapsed, make flyout OPEN
    if (_isStandardsExpanded || isRubricExpanded) {
      navMenuService.toggleFlyoutPanel(closeFirst, FlyoutOpenState.OPEN_WIDER, lessonElementId);
    } else {
      navMenuService.toggleFlyoutPanel(closeFirst, FlyoutOpenState.OPEN, lessonElementId);
    }
  });

  // On mount, check if scores match AI and set state (only if not already set)
  useEffect(() => {
    const checkAndSetIfChangedFromAiScore = async () => {
      if (!lessonElementState) {
        return;
      }
      const hasChangedFromAiScore = typeof lessonElementState.hasChangedFromAiScore === 'boolean' ? (
        lessonElementState.hasChangedFromAiScore
      ) : (
        await scoringService.checkIfChangedFromAiScore(lessonElementId, lessonElementState.scoreState)
      );

      if (hasChangedFromAiScore !== null) {
        lessonElementState.setHasChangedFromAiScore(hasChangedFromAiScore);
      }
    };

    checkAndSetIfChangedFromAiScore();
  }, [lessonElementId]);

  useEffect(async () => {
    if (!lessonElementState) {
      return;
    }
    if (lessonElementState.bedrockUsageId) {
      const tempAiScoreData = await scoringService.fetchAiScoreData(lessonElementState.bedrockUsageId);
      if (tempAiScoreData) {
        setAiScoreData(tempAiScoreData);
      }
    }
  }, [lessonElementState?.bedrockUsageId]);

  // Get AI score data from lesson element state
  return !lessonElementState ? null : (
    <div className='teacher-ai-score-panel flyout-panel' id={`${lessonElementId}-ai-score-panel`}>
      <h2 aria-label={t('teacherAIScoreHeader')} className='panel-header teacher-ai-score-panel-header'>
        {t('teacherAIScoreHeader')}
      </h2>
      <div className='ui-stack'>
        <div className='top-ui'>
          {aiScoreData && aiScoreData.length > 0 && aiScoreData.map((item, index) => (
            <div key={`${index}_${lessonElementId}`}>
              <div aria-label={t('overallScore')} className='resultSubHeader score'>
                {/* TODO: per C2C-7357 discussion - we will only show the AI suggested score in the AI Feedback for now */}
                {/* {lessonElementState.hasChangedFromAiScore === true ? (
                  <>
                    {t('overriddenScore')}: {scoringManager.getTempScore(lessonElementId)} / {maxScore}
                    <div className='ai-score-info'>
                      {t('aiScore')}: {parseFloat(item.overallScore.score).toFixed(2)} / {maxScore}
                    </div>
                  </>
                ) : (
                  `${t('score')}: ${scoringManager.getTempScore(lessonElementId)} / ${maxScore}`
                )} */}
                {`${t('score')}: ${displayScore} / ${maxScore}`}
              </div>
              <div className='score-disclaimer'>
                {/* TODO: per C2C-7357 discussion - we will only show the AI suggested score in the AI Feedback for now */}
                {/* {lessonElementState.hasChangedFromAiScore === true ? (
                  <>
                    {t('originalAiFeedback')}
                    <div className='ai-score-button-wrapper'>
                      <AiSuggestedScoreButton customLabel={t('revertToAiScore')} lessonElementId={lessonElementId} />
                    </div>
                  </>
                ) : (
                  t('aiGeneratedScore')
                )} */}
                {t('aiGeneratedScore')}
              </div>
              {item.rubricCriteria && item.rubricCriteria.length > 0 && model.aiGradingUsesRubric && (
                <div className='expandable-rubric-section-wrapper'>
                  <div aria-label={t('rubricCriteria')} className='resultSubHeader rubric-criteria'>
                    {t('rubricCriteria')}
                    <button ref={rubricToggleButtonRef} className='link-button'>
                      {isRubricExpanded ? t('collapseRubric') : t('expandRubric')}
                    </button>
                  </div>
                  <ExpandableRubricScoringTable
                    aiRubricCriteria={item.rubricCriteria}
                    editable={false}
                    expanded={isRubricExpanded}
                    lessonElementId={lessonElementId}
                    showJustificationWhenCollapsed={true}
                  />
                </div>
              )}

              {item.stateStandards && item.stateStandards.length > 0 && model.aiGradingUsesAlignedStandards && (
                <div className='expandable-standards-section-wrapper'>
                  <div aria-label={t('attachedStandards')} className='resultSubHeader'>
                    {t('attachedStandards')}
                    <button ref={standardsToggleButtonRef} className='link-button'>
                      {isStandardsExpanded ? t('collapseStandards') : t('expandStandards')}
                    </button>
                  </div>
                  <ExpandableStandardsScoringTable
                    expanded={isStandardsExpanded}
                    showStandardId={false}
                    standards={item.stateStandards}
                  />
                </div>
              )}

              {model.aiGradingUsesReadability && item.readingLevel && (
                <>
                  <div aria-label={t('readingLevel')} className='resultSubHeader'>
                    {t('readingLevel')}
                  </div>
                  <div className='dataBlock'>
                    <div aria-label={t('type')} className='resultLabel'>
                      {t('type')}: {item.readingLevel.type}
                    </div>
                    <div aria-label={t('readingLevelValue')} className='resultLabel'>
                      {t('readingLevel')}: {item.readingLevel.level}
                    </div>
                    <div aria-label={t('justification')} className='resultLabel'>
                      {t('justification')}: {item.readingLevel.justification}
                    </div>
                  </div>
                </>
              )}

              {model.aiGradingUsesAiDetection && item.aiDetection && (
                <>
                  <div aria-label={t('aiDetection')} className='resultSubHeader'>
                    {t('aiDetection')}
                  </div>
                  <div className='dataBlock'>
                    <div aria-label={t('likelihood')} className='resultLabel'>
                      {t('likelihood')}: {item.aiDetection.likelihood}%
                    </div>
                    {item.aiDetection.reasoning && item.aiDetection.reasoning.map((reason, reIndex) => (
                      <div key={reIndex} aria-label={t('reason')} className='resultLabel'>
                        {t('reason')}: {reason.reason}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {model.aiGradingUsesAdditionalCriteria && item.additionalCriteria && (
                <>
                  <div aria-label={t('additionalCriteria')} className='resultSubHeader'>
                    {t('additionalCriteria')}
                  </div>
                  <div className='dataBlock'>
                    <div aria-label={t('measurement')} className='resultLabel'>
                      {t('measurement')}: {item.additionalCriteria.measurement}%
                    </div>
                    <div aria-label={t('justification')} className='resultLabel'>
                      {t('justification')}: {item.additionalCriteria.justification}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

export default TeacherAIScoreResultsPanel;
